<template>
  <div
    class="modal fade"
    id="manualsModal"
    data-backdrop="static"
    tabindex="-1"
    role="dialog"
    aria-labelledby="manualsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header shadow-sm bg-dashboard-dark-1">
          <h6
            class="modal-title font-weight-bold text-white"
            id="manualsModalLabel"
          >
            <font-awesome-icon :icon="['fas', 'file-pdf']" class="media-icon mr-2" />
            Manuals
          </h6>
          <button
            type="button"
            class="close text-white"
            aria-label="Close"
            @click="closeModal()"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body bg-dashboard-dark-1 p-0">
          <div class="row m-0" style="min-height: 70vh">
            <div
              class="col-4 col-md-3 p-0 border-right"
              style="border-color: #222222 !important; max-height: 70vh; overflow-y: auto"
            >
              <button
                v-for="manual in manuals"
                :key="manual._id || manual.id"
                class="dropdown-item text-truncate small p-2 text-left"
                :class="
                  currentId === (manual._id || manual.id)
                    ? 'text-white bg-dark'
                    : 'text-light'
                "
                @click="openManual(manual)"
              >
                <font-awesome-icon :icon="['fas', 'file-pdf']" class="mr-2" />
                {{ manual.displayTitle || manual.title }}
              </button>
              <div v-if="!manuals.length" class="small text-muted p-3">
                No manuals available.
              </div>
            </div>
            <div
              class="col p-0 d-flex flex-column"
              style="max-height: 70vh; background: #3a3a3a"
            >
              <div
                v-if="currentUrl && numPages && !loadError"
                class="d-flex align-items-center px-3 py-2 border-bottom small text-light"
                style="border-color: #222222 !important; background: #2b2b2b"
              >
                <span class="text-truncate mr-2" style="flex: 1 1 0">
                  {{ currentTitle }}
                </span>
                <span class="text-nowrap text-center" style="flex: 0 0 auto">
                  <input
                    type="number"
                    :value="currentPage"
                    min="1"
                    :max="numPages"
                    @change="goToPage($event.target.value)"
                    @keyup.enter="goToPage($event.target.value)"
                    class="form-control form-control-sm d-inline-block text-center bg-dark text-light border-secondary px-1 py-0"
                    style="width: 3.5rem; height: 1.6rem"
                  />
                  / {{ numPages }}
                </span>
                <span class="text-right" style="flex: 1 1 0">
                  <button
                    type="button"
                    class="btn btn-sm btn-link text-light p-0"
                    :disabled="downloading || !currentUrl"
                    title="Download PDF"
                    @click="downloadManual()"
                  >
                    <span
                      v-if="downloading"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <font-awesome-icon
                      v-else
                      :icon="['fas', 'file-download']"
                    />
                  </button>
                </span>
              </div>
              <div
                class="flex-grow-1"
                style="overflow: hidden; position: relative"
              >
                <div v-if="loadError" class="text-light text-center p-5">
                  Unable to load this PDF.<br />
                  <small class="text-muted">
                    If this persists, the document host may need to allow access
                    (CORS) from this site.
                  </small>
                </div>
                <template v-else-if="currentUrl">
                  <div
                    v-if="!numPages"
                    class="text-muted text-center p-5"
                    style="position: absolute; top: 0; left: 0; right: 0"
                  >
                    Loading…
                  </div>
                  <pdf-viewer
                    ref="viewer"
                    :key="viewerKey"
                    :url="currentUrl"
                    @loaded="onLoaded"
                    @page-change="currentPage = $event"
                    @error="onError"
                    @page-error="onPageError"
                  />
                </template>
                <div v-else class="text-muted text-center p-5">
                  Select a manual to view.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import { saveAs } from "file-saver";
import API from "@/api";
import PdfViewer from "@/components/PdfViewer";

export default {
  name: "manuals-modal",
  components: { PdfViewer },
  props: {
    show: { type: Boolean, default: false },
    manuals: { type: Array, default: () => [] },
  },
  data() {
    return {
      numPages: 0,
      currentPage: 1,
      currentId: null,
      currentTitle: "",
      currentUrl: "",
      currentName: "",
      downloading: false,
      loadError: false,
      // Incremented per load so retrying the same url remounts the viewer.
      viewerKey: 0,
    };
  },
  watch: {
    show: {
      handler() {
        if (this.show) {
          $("#manualsModal").modal("show");
          this.reset();
          if (this.manuals.length) {
            this.openManual(this.manuals[0]);
          }
        } else {
          $("#manualsModal").modal("hide");
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Bootstrap's own hide paths (Esc) bypass closeModal() and leave the `show`
    // prop untouched, so the watcher above cannot be the release point. This
    // event covers every hide, including the watcher's own modal("hide").
    $("#manualsModal").on("hidden.bs.modal", this.onHidden);
  },
  beforeDestroy() {
    $("#manualsModal").off("hidden.bs.modal", this.onHidden);
  },
  methods: {
    reset() {
      this.numPages = 0;
      this.currentPage = 1;
      this.currentId = null;
      this.currentTitle = "";
      this.currentUrl = "";
      this.currentName = "";
      this.downloading = false;
      this.loadError = false;
    },
    onHidden() {
      // Bootstrap has finished hiding, whatever triggered it — the close
      // button, the `show` watcher, or Esc. Report that upward so `show`
      // tracks actual visibility: Esc otherwise left it true, and because the
      // watcher only fires on a *change*, setting it true again could not
      // reopen the modal.
      this.$emit("close");
      // Clearing currentUrl unmounts <pdf-viewer>, whose beforeDestroy
      // destroys the document and its worker.
      this.reset();
    },
    onLoaded(numPages) {
      this.numPages = numPages || 0;
      this.currentPage = 1;
    },
    openManual(manual) {
      const file = manual && manual.file;
      const url = file && file.url;
      const id = manual._id || manual.id;
      // Ignore a re-click on the manual already selected, whether it has
      // finished loading or is still loading. Two distinct reasons:
      //  - Already loaded: reassigning an IDENTICAL currentUrl is invisible to
      //    Vue (its reactive setter drops an unchanged primitive), so falling
      //    through used to leave numPages at 0 forever, permanently hiding the
      //    page indicator, jump-to-page and download, which are gated on it.
      //  - Still loading: falling through bumps viewerKey and remounts, which
      //    abandons the in-flight fetch and restarts the download from
      //    scratch, so repeatedly clicking a slow manual could stop it ever
      //    finishing.
      // A retry after a FAILED load must still fall through, which is exactly
      // what `!this.loadError` allows.
      if (id === this.currentId && !this.loadError) {
        return;
      }
      this.currentId = id;
      // Bump the key so <pdf-viewer> REMOUNTS. Assigning currentUrl is not
      // enough by itself: retrying a manual that just failed reassigns an
      // IDENTICAL string, and Vue's reactive setter drops an unchanged
      // primitive (hasChanged is a plain ===), so the url watcher never fires
      // and load() is never called again. Without this, clicking a failed
      // manual again clears the error panel and then hangs on "Loading…"
      // forever, with no error and no way back except a different manual.
      // A remount runs mounted(), which loads unconditionally.
      this.viewerKey++;
      this.currentTitle = manual.displayTitle || manual.title || "";
      this.currentName =
        (file && file.name) ||
        (manual.displayTitle || manual.title || "manual") + ".pdf";
      this.numPages = 0;
      this.currentPage = 1;
      this.loadError = false;
      // Assigning currentUrl is what drives the load: <pdf-viewer> watches it
      // and supersedes any load already in flight.
      this.currentUrl = url || "";
      if (!url) this.loadError = true;
    },
    goToPage(value) {
      const target = parseInt(value, 10);
      if (isNaN(target)) return;
      this.currentPage = Math.min(Math.max(target, 1), this.numPages || 1);
      if (this.$refs.viewer) this.$refs.viewer.goToPage(this.currentPage);
    },
    onError(err) {
      // Document-level failure only: PdfViewer emits this from load()'s catch.
      // Log the message explicitly: pdf.js errors take their name from
      // this.constructor.name (pdf.js:2217-2225), which vue-cli's terser
      // mangles to a 1-2 character identifier, so the object alone prints as
      // "[object n]". There is no error-reporting service here, so this line is
      // the entire production diagnosis path.
      console.error("PDF viewer error:", (err && err.message) || err, err);
      this.loadError = true;
    },
    onPageError(err) {
      // One page failed to render. The rest of the document is still good, so
      // this must NOT set loadError — that would swap all the working pages
      // for the error panel.
      console.error(
        "PDF page render error (document still usable):",
        (err && err.message) || err,
        err
      );
    },
    downloadManual() {
      if (!this.currentUrl) {
        return;
      }
      this.downloading = true;
      API.getExternal(this.currentUrl, "file")
        .then((res) => {
          this.downloading = false;
          saveAs(res.data, this.currentName || "manual.pdf");
        })
        .catch((err) => {
          this.downloading = false;
          console.error("Failed to download manual:", err);
          // Fallback: open in a new tab so the user can still get the file.
          window.open(this.currentUrl, "_blank", "noopener");
        });
    },
    closeModal() {
      // Route the button through Bootstrap rather than emitting directly, so
      // hidden.bs.modal stays the single place that reports "closed".
      $("#manualsModal").modal("hide");
    },
  },
};
</script>
